// src/components/GlobeViewer.jsx
import React, { useRef, useCallback, useEffect, useMemo, useState } from 'react';
import DeckGL from '@deck.gl/react';
import { _GlobeView as GlobeView, COORDINATE_SYSTEM, LinearInterpolator } from '@deck.gl/core';
import { ScatterplotLayer, GeoJsonLayer } from '@deck.gl/layers'; 

import { BIOMES_PATH, ECOREGIONS_PATH, PIXEL_SPREAD } from '../constants/Constants.js';
import useElementSize from '../hooks/useElementSize.js';
import { getSingleColor, getMixedColor, handleSharedPointClick, getSharedTooltip, useRemoteGeoJson } from './MapSharedUtils.js';

const hexToRgb = (hex) => {
  if (!hex || hex.length < 7) return [200, 200, 200, 255]; // Fallback gray
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return [r, g, b, 255];
};

const lngLatToXYZ = (lng, lat) => {
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return { x: 0, y: 1, z: 0 };
  const phi = (90 - lat) * (Math.PI / 180);
  const theta = (lng + 180) * (Math.PI / 180);
  return { x: -Math.sin(phi) * Math.sin(theta), y: Math.cos(phi), z: -Math.sin(phi) * Math.cos(theta) };
};

const getOffsetInDegrees = (pixelOffset, zoom, lat) => {
  // Clamp the zoom between a min (e.g. 2.5) and max (e.g. 8)
  const safeZoom = Math.max(2.5, Math.min(Number.isFinite(zoom) ? zoom : 0, 8));
  
  // Guard against extreme latitudes pushing cos(latRadian) to 0 and exploding the offset
  const safeLat = Number.isFinite(lat) ? Math.max(-85, Math.min(lat, 85)) : 0;
  
  const worldSize = 256 * Math.pow(2, safeZoom);
  const latRadian = safeLat * Math.PI / 180;
  
  return { 
    lngDeg: pixelOffset * (360 / (worldSize * Math.cos(latRadian))), 
    latDeg: pixelOffset * (360 / worldSize) 
  };
};

const GlobeViewer = React.memo(function GlobeViewer({
  id, viewState, onViewStateChange, processedData, geojsonFeatures, colorBy, stats, 
  stableColorConfig, isGradient, radiusMinPixels, radiusMaxPixels, lineWidth, onSampleClick,
  selectedSampleIndex, envMode = 'none'
}) {
  const [containerRef, size] = useElementSize({ debounceMs: 50 });
  const width = size?.width || 0;
  const height = size?.height || 0;

  const [expandedId, setExpandedId] = useState(null);
  const [deckKey, setDeckKey] = useState(0); 
  const glContextRef = useRef(null);
  const errorCountRef = useRef(0);

  // Forcefully release the WebGL context on unmount or remount
  useEffect(() => {
    return () => {
      if (glContextRef.current) {
        const ext = glContextRef.current.getExtension('WEBGL_lose_context');
        if (ext) ext.loseContext();
        glContextRef.current = null;
      }
    };
  }, [deckKey, id]);
  const canSpin = false;

  const viewStateRef = useRef(viewState);
  useEffect(() => {
    viewStateRef.current = viewState;
  }, [viewState]);

   // Track the last index we flew to, preventing re-triggers on tab changes
  const prevSampleIndexRef = useRef(null);


  // Auto-Center on selected sample if it is off-screen
  useEffect(() => {
    if (selectedSampleIndex == null) {
      prevSampleIndexRef.current = null;
      return;
    }

    // Only trigger the fly-to logic if the selected sample has actually changed
    if (prevSampleIndexRef.current === selectedSampleIndex) {
      return;
    }

    // Find the coordinates of the selected sample
    let targetPoint = null;
    for (const group of processedData) {
      if (group.allSamples.some(s => s.index === selectedSampleIndex)) {
        targetPoint = group.position;
        break;
      }
    }

    if (targetPoint) {
      // Mark this index as processed so we don't fly to it again unless it changes
      prevSampleIndexRef.current = selectedSampleIndex;

      const [lng, lat] = targetPoint;
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) return; // Guard against NaN

      const currentVS = viewStateRef.current;
      
      // Calculate roughly how many degrees are visible on screen based on zoom
      const visibleDeg = 360 / Math.pow(2, currentVS.zoom);
      
      // Use 80% of the half-width so it triggers a pan even if it's right on the edge of the screen
      const halfDeg = (visibleDeg / 2) * 0.8; 
      
      let dLng = Math.abs(lng - currentVS.longitude);
      if (dLng > 180) dLng = 360 - dLng; // Handle shortest path around the back of the earth
      const dLat = Math.abs(lat - currentVS.latitude);
      
      // 3D vector math to see if it's hidden behind the curve of the globe
      const cameraVec = lngLatToXYZ(currentVS.longitude, currentVS.latitude);
      const ptVec = lngLatToXYZ(lng, lat);
      const dotProd = cameraVec.x * ptVec.x + cameraVec.y * ptVec.y + cameraVec.z * ptVec.z;

      // If it's over the horizon (dotProd <= 0.35) or outside the safe viewing area bounds
      if (dotProd <= 0.35 || dLng > halfDeg || dLat > halfDeg) {
        setIsSpinning(false); // Stop the idle spin if the user clicked a table row
        
        onViewStateChange({
          ...currentVS,
          longitude: lng,
          latitude: lat,
          zoom: currentVS.zoom, // Strictly preserve current zoom
          transitionDuration: 800, // Smooth 800ms rotation
          // Lock the transition to only animate lng/lat, keeping zoom flat
          transitionInterpolator: new LinearInterpolator(['longitude', 'latitude'])
        });
      }
    }
  }, [selectedSampleIndex, processedData, onViewStateChange]);
  
  const rawLandData = useRemoteGeoJson('https://raw.githubusercontent.com/nvkelso/natural-earth-vector/master/geojson/ne_10m_land.geojson');
  const biomesData = useRemoteGeoJson(BIOMES_PATH);
  const ecoregionsData = useRemoteGeoJson(ECOREGIONS_PATH);

  const activeEnvData = useMemo(() => {
    if (envMode === 'biomes') return biomesData;
    if (envMode === 'ecoregions') return ecoregionsData;
    return null;
  }, [envMode, biomesData, ecoregionsData]);


  const [isSpinning, setIsSpinning] = useState(false);
  const requestRef = useRef();
  const lastTimeRef = useRef();


  const visibleGeoJsonFeatures = useMemo(() => {
    if (!geojsonFeatures?.length) return [];
    const cameraVec = lngLatToXYZ(viewState.longitude, viewState.latitude);

    // A ring is "visible" if ANY of its vertices are on the front hemisphere.
    // Using .some() so it short-circuits early on large rings.
    const isRingVisible = (ring) => ring.some(([lng, lat]) => {
      const pt = lngLatToXYZ(lng, lat);
      return (cameraVec.x * pt.x + cameraVec.y * pt.y + cameraVec.z * pt.z) > 0.6;
    });

    return geojsonFeatures.filter(f => {
      const geom = f.geometry;
      if (!geom) return false;
      if (geom.type === 'Polygon')
        return isRingVisible(geom.coordinates[0]);
      if (geom.type === 'MultiPolygon')
        return geom.coordinates.some(poly => isRingVisible(poly[0]));
      return true;
    });
  }, [geojsonFeatures, viewState.longitude, viewState.latitude]);

  const visibleEnvFeatures = useMemo(() => {
    if (!activeEnvData?.features?.length || envMode === 'none') return [];
    const cameraVec = lngLatToXYZ(viewState.longitude, viewState.latitude);

    const isRingVisible = (ring) => ring.some(([lng, lat]) => {
      const pt = lngLatToXYZ(lng, lat);
      return (cameraVec.x * pt.x + cameraVec.y * pt.y + cameraVec.z * pt.z) > 0.4;
    });

    return activeEnvData.features.filter(f => {
      const geom = f.geometry;
      if (!geom) return false;
      if (geom.type === 'Polygon') return isRingVisible(geom.coordinates[0]);
      if (geom.type === 'MultiPolygon') return geom.coordinates.some(poly => isRingVisible(poly[0]));
      return true;
    });
  }, [envMode, activeEnvData, viewState.longitude, viewState.latitude]);

  const starBackground = useMemo(() => {
    let svg = `<svg xmlns="http://www.w3.org/2000/svg" width="1024" height="1024">`;
    for (let i = 0; i < 40; i++) {
      const x = Math.random() * 1024;
      const y = Math.random() * 1024;
      const r = Math.random() * 2.5 + 1.5;
      svg += `<circle cx="${x}" cy="${y}" r="${r}" fill="#ffffff" opacity="${Math.random() * 0.05 + 0.02}"/>`;
    }
    const colors = ['#ffffff', '#ffffff', '#ffffff', '#e0f0ff', '#fff0e0', '#d8ebff'];
    for (let i = 0; i < 500; i++) {
      const x = Math.random() * 1024;
      const y = Math.random() * 1024;
      const r = Math.random() * 1.2 + 0.3;
      const color = colors[Math.floor(Math.random() * colors.length)];
      const opacity = Math.random() * 0.7 + 0.3;
      svg += `<circle cx="${x}" cy="${y}" r="${r}" fill="${color}" opacity="${opacity}"/>`;
    }
    svg += `</svg>`;
    return `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}")`;
  },[]);

  const animate = useCallback(time => {
    if (lastTimeRef.current !== undefined) {
      const deltaTime = time - lastTimeRef.current;
      onViewStateChange({ ...viewState, longitude: ((viewState.longitude + 180 + deltaTime * 0.01) % 360) - 180 });
    }
    lastTimeRef.current = time;
    requestRef.current = requestAnimationFrame(animate);
  }, [viewState, onViewStateChange]);

  useEffect(() => {
    if (isSpinning) requestRef.current = requestAnimationFrame(animate);
    else { cancelAnimationFrame(requestRef.current); lastTimeRef.current = undefined; }
    return () => cancelAnimationFrame(requestRef.current);
  }, [isSpinning, animate]);

  const worldBackground = useMemo(() => ({ type: 'Feature', geometry: { type: 'Polygon', coordinates: [[[-180, -90],[180, -90], [180, 90], [-180, 90],[-180, -90]]] } }),[]);

  const expandedData = useMemo(() => {
    const results = [];
    processedData.forEach(point => {
      const pointId = point.id || `${point.position[0]}-${point.position[1]}`;
      const mixedColor = getMixedColor(point.allValues, isGradient, stats, stableColorConfig);
      results.push({ ...point, mixedColor, pointId, isCenter: true });

      if (pointId === expandedId && point.count > 1) {
        const offset = getOffsetInDegrees(PIXEL_SPREAD, viewState.zoom, point.position[1]);
        point.allSamples.forEach((sample, i) => {
          const angle = (i / point.count) * 2 * Math.PI;
          const newPos = [ point.position[0] + Math.cos(angle) * offset.lngDeg, point.position[1] + Math.sin(angle) * offset.latDeg ];
          results.push({
            ...point, isChild: true, displayVal: sample.value, sampleIndex: sample.index,
            mixedColor: getSingleColor(sample.value, isGradient, stats, stableColorConfig), position: newPos, isCenter: false
          });
        });
      }
    });
    return results;
  }, [processedData, expandedId, (expandedId ? viewState.zoom : null), isGradient, stats, stableColorConfig]);

  const { binaryAttributes, visibleLookup } = useMemo(() => {
    const cameraVec = lngLatToXYZ(viewState.longitude, viewState.latitude);
    
    // Separate normal points and selected points
    const normalIndices = [];
    const selectedIndices = [];
    
    for (let i = 0; i < expandedData.length; i++) {
      const d = expandedData[i];
      const ptVec = lngLatToXYZ(d.position[0], d.position[1]);
      
      // Check if visible on the front of the globe
      if ((cameraVec.x * ptVec.x + cameraVec.y * ptVec.y + cameraVec.z * ptVec.z) > 0.3) {
        const isExpandedCenter = d.isCenter && expandedId === d.pointId && d.count > 1;
        
        // Determine if this point represents the selected sample
        let isSelected = false;
        if (selectedSampleIndex != null) {
          if (d.isChild) {
            isSelected = d.sampleIndex === selectedSampleIndex;
          } else if (!isExpandedCenter) {
            isSelected = d.allSamples.some(s => s.index === selectedSampleIndex);
          }
        }

        // Put selected items in a separate bucket
        if (isSelected) {
          selectedIndices.push(i);
        } else {
          normalIndices.push(i);
        }
      }
    }

    // Concatenate so selected items are at the end of the array
    // This forces the GPU to draw them last, meaning they are always on top.
    const visibleIndices = [...normalIndices, ...selectedIndices, ...selectedIndices];

    const n = visibleIndices.length;
    const pos = new Float32Array(n * 2);
    const fill = new Uint8Array(n * 4);
    const line = new Uint8Array(n * 4);
    const rad = new Float32Array(n);
    const lw = new Float32Array(n); 
    const lookup = new Array(n);

    const numNormal = normalIndices.length;
    const numSelected = selectedIndices.length;

    for (let i = 0; i < n; i++) {
      const d = expandedData[visibleIndices[i]];
      pos[i * 2] = Number.isFinite(d.position[0]) ? d.position[0] : 0; 
      pos[i * 2 + 1] = Number.isFinite(d.position[1]) ? d.position[1] : 0;
      
      const isExpandedCenter = d.isCenter && expandedId === d.pointId && d.count > 1;
      const fillColor = isExpandedCenter ? [255, 255, 255, 255] : d.mixedColor;
      
      // Every point gets its normal fill color
      fill.set(fillColor, i * 4);
      
      if (i < numNormal) {
        // --- NORMAL POINTS ---
        const actualLineWidth = lineWidth || 0;
        
        if (actualLineWidth === 0) {
          line.set([fillColor[0], fillColor[1], fillColor[2], 0], i * 4); // Anti-alias fix
        } else {
          line.set(isExpandedCenter ?[0, 0, 0, 255] :[255, 255, 255, 200], i * 4);
        }
        
        lw[i] = actualLineWidth;
        rad[i] = d.isChild ? 4 : Math.sqrt(d.count);

      } else if (i < numNormal + numSelected) {
        // ---  HIGHLIGHT UNDERLAY (Outer Border) ---
        // White border, replace with [0, 0, 0, 255] for a black border instead
        line.set([255, 255, 255, 255], i * 4); 
        
        // Thicker stroke so it sticks out past the red border
        lw[i] = Math.max(lineWidth || 0, d.isChild ? 2 : 3) + 3; 
        rad[i] = (d.isChild ? 4 : Math.sqrt(d.count)) + 2; 

      } else {
        // ---  HIGHLIGHT OVERLAY (Inner Border) ---
        // Red border
        line.set([245, 73, 39, 255], i * 4); 
        
        // Normal stroke thickness
        lw[i] = Math.max(lineWidth || 0, d.isChild ? 2 : 3); 
        rad[i] = (d.isChild ? 4 : Math.sqrt(d.count)) + 2; 
      }
      
      lookup[i] = d;
    }

    return {
      binaryAttributes: { 
        instancePositions: { value: pos, size: 2 }, 
        instanceFillColors: { value: fill, size: 4, normalized: true }, 
        instanceLineColors: { value: line, size: 4, normalized: true }, 
        instanceLineWidths: { value: lw, size: 1 }, 
        instanceRadius: { value: rad, size: 1 } 
      },
      visibleLookup: lookup
    };
  }, [expandedData, viewState.longitude, viewState.latitude, expandedId, selectedSampleIndex, lineWidth]);

  const layers = useMemo(() => [
    new GeoJsonLayer({ id: 'globe-ocean', data: worldBackground, getFillColor: [183, 223, 249], pickable: false }),
    new GeoJsonLayer({ id: 'globe-land', data: rawLandData, getFillColor: [40, 45, 60], getLineColor: [80, 90, 110], lineWidthMinPixels: 0.5, pickable: false }),
    new GeoJsonLayer({
      id: 'globe-env',
      data: { type: 'FeatureCollection', features: visibleEnvFeatures },
      getFillColor: f => hexToRgb(f.properties.COLOR || f.properties.COLOR_BIO || f.properties.COLOR_ECO), 
      getLineColor: f => hexToRgb(f.properties.COLOR || f.properties.COLOR_BIO || f.properties.COLOR_ECO),
      pickable: envMode !== 'none',
      lineWidthMinPixels: 0.5,
      parameters: { depthTest: false }
    }),
    new GeoJsonLayer({
      id: `globe-geojson-${id}`,
      data: { type: 'FeatureCollection', features: visibleGeoJsonFeatures },
      lineWidthMinPixels: lineWidth,
      lineWidthMaxPixels: lineWidth,
      pickable: true,
      stroked: true,
      parameters: { depthTest: false },
      getFillColor: f => getSingleColor(f.properties[colorBy], isGradient, stats, stableColorConfig),
      getLineColor: f => getSingleColor(f.properties[colorBy], isGradient, stats, stableColorConfig),
      updateTriggers: { getFillColor: [colorBy, isGradient, stats, stableColorConfig], getLineColor: [colorBy, isGradient, stats, stableColorConfig] }
    }),
    new ScatterplotLayer({
      id: `globe-points-${id}`, 
      data: { length: visibleLookup.length, attributes: binaryAttributes }, 
      pickable: true, 
      stroked: true, // Always true to permit red border mapping
      getLineWidth: 1, // Triggers binary instanceLineWidths accessor
      lineWidthUnits: 'pixels', // Treat width mapping strictly as pixels
      lineWidthMinPixels: 0, // 0 allows unselected points to be borderless if 'lineWidth' settings is 0
      lineWidthMaxPixels: 10,
      radiusUnits: 'pixels', 
      radiusMaxPixels, 
      radiusMinPixels,
      onClick: (info) => handleSharedPointClick(info, visibleLookup, onSampleClick, setExpandedId),
      coordinateSystem: COORDINATE_SYSTEM.LNGLAT, 
      parameters: { depthTest: false }, 
      updateTriggers: { attributes: [binaryAttributes] }
    })
  ], [id, worldBackground, rawLandData, visibleEnvFeatures, visibleGeoJsonFeatures, colorBy, isGradient, stats, stableColorConfig, lineWidth, radiusMaxPixels, radiusMinPixels, binaryAttributes, visibleLookup, onSampleClick]);

  return (
    <div 
      ref={containerRef} 
      className="w-full h-full relative overflow-hidden"
      style={{
        backgroundColor: '#000000', 
        backgroundImage: starBackground,
        backgroundPosition: `${(viewState?.longitude || 0) * 2.5}px ${(viewState?.latitude || 0) * 2.5}px`,
        backgroundRepeat: 'repeat'
      }}
    >

      {canSpin && (
        <button onClick={() => setIsSpinning(!isSpinning)} className="absolute top-4 right-4 z-[1001] p-2 bg-white/10 hover:bg-white/20 border border-white/20 rounded-full text-white backdrop-blur-md transition-all">
          {isSpinning ? <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg> : <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
        </button>
      )}
      
      {/* Prevents rendering 0-size WebGL canvases during transition, avoiding silent crashes */}
      {width > 0 && height > 0 && (
        <DeckGL
          key={`deck-globe-${id}-${deckKey}`}
          views={new GlobeView({ id: 'globe', controller: true, resolution: 2 })} // increase resolution to improve performances
          viewState={viewState}
          pickingRadius={15} // easier to click small points on the globe
          onWebGLInitialized={(gl) => {
            glContextRef.current = gl;
            errorCountRef.current = 0; // Reset consecutive errors
          }}
          onViewStateChange={e => {
            if (Number.isFinite(e.viewState.longitude) && Number.isFinite(e.viewState.latitude)) {
              onViewStateChange(e.viewState);
            }
          }} 
          onDragStart={() => isSpinning && setIsSpinning(false)} 
          layers={layers} 
          getTooltip={(info) => {
          if (info.layer?.id === 'globe-env' && info.object) {
            const label = envMode === 'biomes' ? 'Biome' : 'Ecoregion';
            const name = info.object.properties.BIOME_NAME || info.object.properties.ECO_NAME;
            return {
              html: `<div style="padding: 0px; font-size: 9px">
                      <b style="font-size: 9px; color: #000000;">${label}</b><br/>
                      ${name}
                    </div>`,
              style: { backgroundColor: '#fff', color: '#000', borderRadius: '8px' }
            };
          }
          return getSharedTooltip(info, visibleLookup, expandedId, colorBy);
        }}
          onContextLost={(e) => {
            e.preventDefault();
            console.warn(`[GlobeViewer ${id}] Context lost. Attempting restore...`);
            if (errorCountRef.current < 5) {
              errorCountRef.current++;
              setTimeout(() => setDeckKey(k => k + 1), 250);
            }
          }}
          onError={(error) => {
            console.error(`[GlobeViewer ${id}] DeckGL Error:`, error);
            if (error && error.message && error.message.toLowerCase().includes('webgl')) {
              setDeckKey(k => k + 1);
            }
          }}
        />
      )}
    </div>
  );
});

export default GlobeViewer;